import { useState } from "react";
import WhiteLabelChatPanel from "./WhiteLabelChatPanel";
import DevTenantDebug from "../components/DevTenantDebug";
import { useClinicProfile } from "../hooks/useClinicProfile";
import { getVetcanApiBaseUrl } from "../lib/apiBase";
import { type WhiteLabelTenantConfig } from "../lib/tenantConfig";

function toTelHref(phone: string) {
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 10) return "#";
  return `tel:+${digits}`;
}

function toLocationLines(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return [];
  if (trimmed.includes("\n")) {
    return trimmed
      .split(/\n+/)
      .map((part) => part.trim())
      .filter(Boolean);
  }
  return [trimmed];
}

function toHoursLines(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return [];
  if (trimmed.includes("\n")) {
    return trimmed
      .split(/\n+/)
      .map((part) => part.trim())
      .filter(Boolean);
  }
  return [trimmed];
}

function getClinicInitials(clinicName: string) {
  const words = clinicName
    .split(/\s+/)
    .map((word) => word.trim())
    .filter(Boolean);
  const firstTwo = words.slice(0, 2).map((word) => word[0] || "");
  return firstTwo.join("").toUpperCase() || "CL";
}

export default function WhiteLabelLanding({
  tenant,
  tenantKey,
}: {
  tenant: WhiteLabelTenantConfig;
  tenantKey: string;
}) {
  const {
    tenantKey: resolvedTenantKey,
    profile,
    source,
    isLoading: profileLoading,
    devNotice,
    lastFetchStatus,
    lastFetchAt,
  } = useClinicProfile({ tenantKey });
  const [logoVisible, setLogoVisible] = useState(Boolean(tenant.logoPath));

  const clinicName = profile.displayName;
  const renewalsPhone = profile.renewalsPhone;
  const supportPhone = profile.supportPhone;
  const noMedicalAdviceNotice = profile.noMedicalAdviceNotice;
  const locationLines = toLocationLines(profile.addressText);
  const hoursLines = toHoursLines(profile.hoursText);
  const runtimeHost =
    typeof window !== "undefined" ? window.location.host : resolvedTenantKey;

  const clinicFacts = [
    { label: "Hours", value: hoursLines.join("\n") },
    { label: "Location", value: locationLines.join("\n") },
    { label: "Parking", value: profile.parkingText },
    {
      label: "Renew by telephone (no video)",
      value: renewalsPhone,
    },
    {
      label: "Call or text 24/7",
      value: supportPhone,
    },
  ];

  const faqItems = [
    {
      question: "Can I schedule a new appointment here?",
      answer:
        "Yes. Use chat to review available times and complete scheduling when the path is clear. If payment is required, secure payment options can be surfaced. If booking is unavailable or unclear, staff follow-up is queued.",
    },
    {
      question: "How do renewals work?",
      answer:
        "Yes. Share your renewal request and preferred contact method, and we will route it to staff.",
    },
    {
      question: "Can I ask about hours, location, and parking?",
      answer:
        "Yes. Chat can answer operational questions and route anything that needs staff confirmation.",
    },
    {
      question: "Can I call or text instead of chat?",
      answer:
        `Yes. Call or text ${supportPhone}, or use ${renewalsPhone} to renew by telephone without video.`,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-emerald-50 text-slate-900">
      <header className="border-b border-emerald-100 bg-white/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            {logoVisible && tenant.logoPath ? (
              <img
                src={tenant.logoPath}
                alt={`${clinicName} logo`}
                className="h-10 w-auto"
                onError={() => {
                  setLogoVisible(false);
                }}
              />
            ) : (
              <div
                aria-label={`${clinicName} logo fallback`}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-xs font-semibold text-emerald-800"
              >
                {getClinicInitials(clinicName)}
              </div>
            )}
            <div>
              <p className="text-lg font-semibold text-emerald-900">{clinicName}</p>
              <p className="text-xs text-slate-600">Clinic scheduling and support</p>
            </div>
          </div>
          <div className="flex flex-wrap justify-end gap-3">
            <a
              href="/chat"
              className="rounded-full bg-emerald-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-800"
            >
              Open Full Chat
            </a>
            <a
              href={toTelHref(supportPhone)}
              className="rounded-full border border-emerald-200 px-4 py-2 text-sm font-semibold text-emerald-900 transition hover:bg-emerald-50"
            >
              Call or text 24/7: {supportPhone}
            </a>
            <a
              href={toTelHref(renewalsPhone)}
              className="rounded-full border border-emerald-200 px-4 py-2 text-sm font-semibold text-emerald-900 transition hover:bg-emerald-50"
            >
              Renew by telephone (no video): {renewalsPhone}
            </a>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl px-6 py-10">
        {import.meta.env.DEV && devNotice ? (
          <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {devNotice}
          </div>
        ) : null}
        <section className="rounded-3xl border border-emerald-100 bg-white p-8 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">
            {clinicName}
          </p>
          <h1 className="mt-3 text-4xl font-semibold text-emerald-950">
            Fast answers for hours, location, scheduling, and renewals.
          </h1>
          <p className="mt-4 max-w-3xl text-base text-slate-700">
            Start in chat to review scheduling options and complete next steps
            quickly. If payment is required, secure payment options can be provided.
            When booking is unavailable or unclear, staff follow-up is queued.
          </p>
          <p className="mt-3 text-sm text-slate-600">{noMedicalAdviceNotice}</p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {clinicFacts.map((fact) => (
              <div
                key={fact.label}
                className="rounded-2xl border border-emerald-100 bg-emerald-50/60 px-4 py-3"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-emerald-700">
                  {fact.label}
                </p>
                <p className="mt-1 whitespace-pre-line text-sm text-slate-700">
                  {fact.value}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="mb-4 text-2xl font-semibold text-emerald-950">
            Chat with {clinicName}
          </h2>
          <WhiteLabelChatPanel
            tenant={tenant}
            clinicProfile={profile}
            profileLoading={profileLoading}
            tenantKey={resolvedTenantKey}
            mode="widget"
          />
        </section>

        <section className="mt-10 rounded-3xl border border-emerald-100 bg-white p-8 shadow-sm">
          <h2 className="text-2xl font-semibold text-emerald-950">Clinic FAQ</h2>
          <div className="mt-5 space-y-3">
            {faqItems.map((item) => (
              <details
                key={item.question}
                className="rounded-2xl border border-emerald-100 bg-emerald-50/40 px-5 py-4"
              >
                <summary className="cursor-pointer text-sm font-semibold text-slate-900">
                  {item.question}
                </summary>
                <p className="mt-2 text-sm text-slate-700">{item.answer}</p>
              </details>
            ))}
          </div>
        </section>
      </main>
      {import.meta.env.DEV ? (
        <DevTenantDebug
          host={runtimeHost}
          tenantKey={resolvedTenantKey}
          source={source}
          clinicName={clinicName}
          supportPhone={supportPhone}
          apiBase={getVetcanApiBaseUrl()}
          lastFetchStatus={lastFetchStatus}
          lastFetchAt={lastFetchAt}
        />
      ) : null}
    </div>
  );
}
